import React, { useEffect, useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  LinearProgress,
  Avatar,
  Popover,
  Paper,
  Divider,
} from "@mui/material";
import {
  People,
  Assignment,
  Visibility,
  Add,
  CheckCircle,
  Schedule,
  Analytics,
  Work,
  Assessment,
  Autorenew,
  CloudDownload,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

import { getAdminDashboardData } from "../../api/appraisalApi";
import { appraisalState } from "../appraisal/appraisalSlice";
import { Button } from "../../components/Button";
import Input from "../../components/Input";
import DateInput from "../../components/DateInput";
import * as XLSX from "xlsx";

// Enhanced Metric Card Component
const EnhancedMetricCard = ({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  color = "primary",
  subtitle,
  filterData,
  onFilterClick,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const handleMouseEnter = (event) => {
    if (filterData && filterData.length > 0) {
      // Small delay before showing tooltip
      const timeout = setTimeout(() => {
        setAnchorEl(event.currentTarget);
      }, 300);
      setHoverTimeout(timeout);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const colorConfigs = {
    // Brand-tuned palettes (blue/orange emphasis), with softer shadows
    primary: {
      bg: "linear-gradient(to bottom right, #0b3a5c, #135e96, #1f7fbb)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(19,94,150,0.35)",
    },
    success: {
      bg: "linear-gradient(to bottom right, #0f766e, #0d9488, #14b8a6)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(20,184,166,0.28)",
    },
    warning: {
      bg: "linear-gradient(to bottom right, #B45309, #D97706, #F5A623)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(245,166,35,0.28)",
    },
    error: {
      bg: "linear-gradient(to bottom right, #14532d, #166534, #22c55e)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(34,197,94,0.26)",
    },
    info: {
      bg: "linear-gradient(to bottom right, #5b21b6, #6d28d9, #8b5cf6)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(107,70,193,0.26)",
    },
  };

  const config = colorConfigs[color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.02, y: -5 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Card
        className="h-full transition-all duration-300 hover:shadow-2xl group overflow-hidden relative cursor-pointer"
        style={{
          background: config.bg,
          boxShadow: config.shadow,
          borderRadius: "20px",
          border: "none",
          minHeight: "140px",
          position: "relative",
          overflow: "visible",
        }}
        onClick={() => onFilterClick && onFilterClick()}
      >
        {/* Animated background gradient overlay */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-white/10 to-transparent"></div>
        
        {/* Shine effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        </div>

        <CardContent className="p-6 relative z-10 h-full">
          <div className="flex flex-col h-full">
            {/* Title and Subtitle */}
            <div className="mb-3">
              <Typography
                variant="body2"
                className="text-white text-opacity-90 font-medium mb-1"
                style={{ fontSize: "13px", letterSpacing: "0.5px" }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  className="text-white text-opacity-70"
                  style={{ fontSize: "11px" }}
                >
                  {subtitle}
                </Typography>
              )}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-end justify-between">
              {/* Value */}
              <div>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <Typography
                    variant="h3"
                    className="font-bold text-white"
                    style={{
                      fontSize: "36px",
                      textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                      lineHeight: 1.1,
                      fontWeight: 700,
                    }}
                  >
                    {value || 0}
                  </Typography>
                </motion.div>

                {/* Change indicator */}
                {change !== undefined && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Typography
                      variant="body2"
                      className="text-white text-opacity-90 mt-2 flex items-center gap-1"
                      style={{ fontSize: "12px" }}
                    >
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${
                          changeType === "increase"
                            ? "bg-green-300"
                            : changeType === "decrease"
                            ? "bg-red-300"
                            : "bg-gray-300"
                        }`}
                      ></span>
                      {(() => {
                        if (changeType === "increase") return "+";
                        if (changeType === "decrease") return "-";
                        return "";
                      })()}
                      {change}% vs last period
                    </Typography>
                  </motion.div>
                )}
              </div>

              {/* Icon with enhanced animation */}
              <motion.div
                className="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:rotate-12"
                style={{
                  background: config.iconBg,
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                }}
                whileHover={{ scale: 1.15, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Icon style={{ color: "white", fontSize: "28px" }} />
              </motion.div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter Tooltip/Popover */}
      {filterData && filterData.length > 0 && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleMouseLeave}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          disableRestoreFocus
          sx={{
            pointerEvents: "none",
            "& .MuiPopover-paper": {
              pointerEvents: "auto",
            },
          }}
        >
          <Paper
            sx={{
              p: 2,
              minWidth: 250,
              maxWidth: 350,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: "12px",
            }}
            onMouseEnter={() => setAnchorEl(anchorEl)}
            onMouseLeave={handleMouseLeave}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "#1f2937",
                fontSize: "14px",
              }}
            >
              {title} Breakdown
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
              {filterData.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    px: 1,
                    borderRadius: "6px",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                      cursor: "pointer",
                    },
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => {
                    if (onFilterClick && item.filterKey) {
                      onFilterClick(item.filterKey);
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "13px",
                      color: "#4b5563",
                      fontWeight: 500,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "13px",
                      color: "#1f2937",
                      fontWeight: 600,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
            {onFilterClick && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11px",
                    color: "#6b7280",
                    fontStyle: "italic",
                  }}
                >
                  Click to view filtered list
                </Typography>
              </>
            )}
          </Paper>
        </Popover>
      )}
    </motion.div>
  );
};

EnhancedMetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  change: PropTypes.number,
  changeType: PropTypes.oneOf(["increase", "decrease", "neutral"]),
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.oneOf(["primary", "success", "warning", "error", "info"]),
  subtitle: PropTypes.string,
  filterData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      filterKey: PropTypes.string,
    })
  ),
  onFilterClick: PropTypes.func,
};


// Quick Action Card Component
const QuickActionCard = ({
  title,
  description,
  icon: Icon,
  onClick,
  color = "primary",
}) => {
  const colorConfigs = {
    primary: {
      bg: "linear-gradient(to bottom right, #5b21b6, #6d28d9, #8b5cf6)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(107,70,193,0.28)",
    },
    success: {
      bg: "linear-gradient(to bottom right, #0f766e, #0d9488, #14b8a6)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(20,184,166,0.26)",
    },
    warning: {
      bg: "linear-gradient(to bottom right, #B45309, #D97706, #F5A623)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(245,166,35,0.26)",
    },
    error: {
      bg: "linear-gradient(to bottom right, #0b3a5c, #135e96, #1f7fbb)",
      iconBg: "rgba(255,255,255,0.18)",
      shadow: "0 10px 28px rgba(19,94,150,0.28)",
    },
  };

  const config = colorConfigs[color];

  return (
    <Card
      className="cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group overflow-hidden relative"
      onClick={onClick}
      style={{
        background: config.bg,
        boxShadow: config.shadow,
        borderRadius: "16px",
        border: "none",
      }}
    >
      <CardContent className="p-6 relative z-10 h-full">
        <div className="flex flex-col items-center text-center h-full">
          {/* Icon */}
          <div
            className="w-14 h-14 rounded-lg mb-4 flex items-center justify-center transition-all duration-300 group-hover:scale-110"
            style={{
              background: config.iconBg,
              backdropFilter: "blur(10px)",
            }}
          >
            <Icon style={{ color: "white", fontSize: "28px" }} />
          </div>

          {/* Title */}
          <Typography
            variant="h6"
            className="font-bold text-white mb-2"
            style={{
              fontSize: "16px",
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            className="text-white text-opacity-90"
            style={{ fontSize: "12px", lineHeight: 1.4 }}
          >
            {description}
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
};

QuickActionCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func.isRequired,
  color: PropTypes.oneOf(["primary", "success", "warning", "error"]),
};


// Status Distribution Chart
const StatusDistributionChart = ({ data = [] }) => {
  // Using the same colors as the metric cards
  const SOLID_COLORS = [
    "#8B5CF6", // Purple (primary)
    "#14B8A6", // Teal (success)
    "#F59E0B", // Orange (warning)
    "#10B981", // Green (error)
    "#3B82F6", // Blue (info)
    "#EF4444", // Red
    "#F97316", // Orange-600
    "#84CC16", // Lime
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        className="h-full overflow-hidden relative group"
        style={{
          background: "white",
          borderRadius: "24px",
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
        }}
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/30 to-pink-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        <CardContent className="p-6 relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <motion.div
              className="p-3 rounded-xl bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 shadow-lg"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Assessment style={{ color: "white", fontSize: "24px" }} />
            </motion.div>
            <div>
              <Typography variant="h6" className="font-bold text-gray-800 mb-0.5">
                Appraisal Status Distribution
              </Typography>
              <Typography variant="caption" className="text-gray-500 text-xs">
                Current status breakdown
              </Typography>
            </div>
          </div>
          <div className="h-64">
            {data && data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <defs>
                    {SOLID_COLORS.map((color, index) => (
                      <linearGradient
                        key={`gradient-${color}`}
                        id={`gradient-${index}`}
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop offset="0%" stopColor={color} stopOpacity={0.9} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.5} />
                      </linearGradient>
                    ))}
                  </defs>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={75}
                    outerRadius={115}
                    paddingAngle={4}
                    dataKey="value"
                    stroke="white"
                    strokeWidth={3}
                    animationBegin={0}
                    animationDuration={1000}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={`url(#gradient-${index % SOLID_COLORS.length})`}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={{
                      background: "rgba(255,255,255,0.98)",
                      border: "none",
                      borderRadius: "16px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                      backdropFilter: "blur(10px)",
                      padding: "12px",
                    }}
                    formatter={(value, name) => [value, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center mb-4">
                  <Assessment style={{ color: "#8B5CF6", fontSize: "40px" }} />
                </div>
                <Typography variant="body2" className="text-gray-500 font-medium">
                  No data available
                </Typography>
                <Typography variant="caption" className="text-gray-400 text-xs mt-1">
                  Status distribution will appear here
                </Typography>
              </div>
            )}
          </div>
          
          {/* Legend */}
          {data && data.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3 justify-center">
              {data.slice(0, 4).map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      background: SOLID_COLORS[index % SOLID_COLORS.length],
                    }}
                  ></div>
                  <Typography variant="caption" className="text-gray-600 text-xs font-medium">
                    {entry.name}
                  </Typography>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

StatusDistributionChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
};

// Performance Trends Chart
const PerformanceTrendsChart = ({ data = [] }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        className="h-full overflow-hidden relative group"
        style={{
          background: "white",
          borderRadius: "24px",
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
        }}
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-blue-50/30 to-teal-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        <CardContent className="p-6 relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <motion.div
                className="p-3 rounded-xl bg-gradient-to-br from-green-500 via-blue-600 to-teal-500 shadow-lg"
                whileHover={{ scale: 1.1, rotate: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Analytics style={{ color: "white", fontSize: "24px" }} />
              </motion.div>
              <div>
                <Typography variant="h6" className="font-bold text-gray-800 mb-0.5">
                  Performance Trends
                </Typography>
                <Typography variant="caption" className="text-gray-500 text-xs">
                  Historical performance metrics
                </Typography>
              </div>
            </div>
            <Tooltip title="View detailed trends">
              <IconButton
                size="small"
                className="bg-gradient-to-r from-green-100 to-blue-100 hover:from-green-200 hover:to-blue-200 transition-all duration-200 shadow-sm"
              >
                <Analytics style={{ color: "#10B981", fontSize: "20px" }} />
              </IconButton>
            </Tooltip>
          </div>
          <div className="h-[420px] min-h-[420px]">
            {!data || data.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <motion.div
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center mb-4"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <Analytics style={{ color: "#10B981", fontSize: "40px" }} />
                </motion.div>
                <Typography variant="body2" className="text-gray-500 font-medium mb-1">
                  No performance trend data available
                </Typography>
                <Typography variant="caption" className="text-gray-400 text-xs">
                  Historical performance data will be displayed here once available
                </Typography>
              </div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient
                  id="excellentGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#11998e" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#11998e" stopOpacity={0.3} />
                </linearGradient>
                <linearGradient id="goodGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#667eea" stopOpacity={0.3} />
                </linearGradient>
                <linearGradient
                  id="averageGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#f093fb" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#f093fb" stopOpacity={0.3} />
                </linearGradient>
                <linearGradient
                  id="fairGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#FFA726" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#FFA726" stopOpacity={0.3} />
                </linearGradient>
                <linearGradient
                  id="poorGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#EF5350" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#EF5350" stopOpacity={0.3} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis
                dataKey="name"
                tick={{ fill: "#6b7280", fontSize: 12 }}
                axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
              />
              <YAxis
                tick={{ fill: "#6b7280", fontSize: 12 }}
                axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                domain={[0, 100]}
              />
              <RechartsTooltip
                formatter={(value, name, props) => {
                  // Get the count from the payload
                  const payload = props.payload;
                  let count = 0;
                  if (name === "Excellent") {
                    count = payload.excellentCount || 0;
                  } else if (name === "Exceeds Expectations") {
                    count = payload.goodCount || 0;
                  } else if (name === "Meets Expectations") {
                    count = payload.averageCount || 0;
                  } else if (name === "Needs Improvement") {
                    count = payload.fairCount || 0;
                  } else if (name === "Unsatisfactory") {
                    count = payload.poorCount || 0;
                  }
                  return [`${count} (${value}%)`, name];
                }}
                labelFormatter={(label) => label}
                contentStyle={{
                  background: "rgba(255,255,255,0.95)",
                  border: "none",
                  borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  backdropFilter: "blur(10px)",
                }}
              />
              <Bar
                dataKey="excellent"
                fill="url(#excellentGradient)"
                radius={[4, 4, 0, 0]}
                name="Excellent"
              />
              <Bar
                dataKey="good"
                fill="url(#goodGradient)"
                radius={[4, 4, 0, 0]}
                name="Exceeds Expectations"
              />
              <Bar
                dataKey="average"
                fill="url(#averageGradient)"
                radius={[4, 4, 0, 0]}
                name="Meets Expectations"
              />
              <Bar
                dataKey="fair"
                fill="url(#fairGradient)"
                radius={[4, 4, 0, 0]}
                name="Needs Improvement"
              />
              <Bar
                dataKey="poor"
                fill="url(#poorGradient)"
                radius={[4, 4, 0, 0]}
                name="Unsatisfactory"
              />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#11998e" }}></div>
            <Typography variant="caption" className="text-gray-600 font-medium">
              Excellent
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#667eea" }}></div>
            <Typography variant="caption" className="text-gray-600 font-medium">
              Exceeds Expectations
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#f093fb" }}></div>
            <Typography variant="caption" className="text-gray-600 font-medium">
              Meets Expectations
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FFA726" }}></div>
            <Typography variant="caption" className="text-gray-600 font-medium">
              Needs Improvement
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#EF5350" }}></div>
            <Typography variant="caption" className="text-gray-600 font-medium">
              Unsatisfactory
            </Typography>
          </div>
        </div>
      </CardContent>
    </Card>
    </motion.div>
  );
};

PerformanceTrendsChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      excellent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      good: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      fair: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      poor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })
  ),
};


// Recent Activity Component
const RecentActivity = ({ activities = [] }) => {
  return (
    <Card
      className="h-full overflow-hidden relative"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
        backdropFilter: "blur(10px)",
        borderRadius: "20px",
        border: "1px solid rgba(255,255,255,0.2)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-red-50 opacity-50"></div>
      <CardContent className="p-6 relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-600">
              <Schedule style={{ color: "white" }} />
            </div>
            <Typography variant="h6" className="font-bold text-gray-800">
              Recent Activity
            </Typography>
          </div>
          <Tooltip title="View all activities">
            <IconButton
              size="small"
              className="bg-white bg-opacity-50 hover:bg-opacity-80 transition-all duration-200"
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        </div>
        <div className="space-y-4">
          {activities.length > 0 ? (
            activities.map((activity) => (
              <div
                key={activity.title}
                className="flex items-start gap-4 p-3 rounded-xl hover:bg-white hover:bg-opacity-50 transition-all duration-200 group"
              >
                <div className="relative">
                  <Avatar
                    className="w-10 h-10 transition-all duration-300 group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, ${activity.color} 0%, ${activity.color}CC 100%)`,
                      boxShadow: `0 4px 12px ${activity.color}40`,
                    }}
                  >
                    {activity.icon}
                  </Avatar>
                  <div
                    className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white"
                    style={{ backgroundColor: activity.color }}
                  ></div>
                </div>
                <div className="flex-1">
                  <Typography
                    variant="body2"
                    className="font-semibold text-gray-800 mb-1"
                  >
                    {activity.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    className="text-gray-500 font-medium"
                  >
                    {activity.time}
                  </Typography>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Schedule style={{ color: "#9ca3af", fontSize: "32px" }} />
              </div>
              <Typography variant="body2" className="text-gray-500 font-medium">
                No recent activities
              </Typography>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

RecentActivity.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
      color: PropTypes.string.isRequired,
    })
  ),
};


const EnhancedAdminDashboard = () => {
  const dispatch = useDispatch();
  const adminDashboardData = useSelector(appraisalState);
  const [dates, setDates] = useState({
    startDate: "",
    endDate: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchButtonClicked, setSearchButtonClicked] = useState(false);

  // Performance trends data from API
  const performanceTrendData = useMemo(() => {
    const data = adminDashboardData?.adminDataCounts || {};
    const excellent = Number(data.excellent) || 0;
    const good = Number(data.good) || 0;
    const average = Number(data.average) || 0;
    const fair = Number(data.fair) || 0;
    const poor = Number(data.poor) || 0;
    
    // Calculate total for percentage calculation (include all ratings)
    const total = excellent + good + average + fair + poor;
    
    // Always return data to show all ratings, even if all are 0
    return [
      {
        name: "Current Period",
        excellent: total > 0 ? Number(((excellent / total) * 100).toFixed(1)) : 0,
        good: total > 0 ? Number(((good / total) * 100).toFixed(1)) : 0,
        average: total > 0 ? Number(((average / total) * 100).toFixed(1)) : 0,
        fair: total > 0 ? Number(((fair / total) * 100).toFixed(1)) : 0,
        poor: total > 0 ? Number(((poor / total) * 100).toFixed(1)) : 0,
        // Store original counts for tooltip
        excellentCount: excellent,
        goodCount: good,
        averageCount: average,
        fairCount: fair,
        poorCount: poor,
      }
    ];
  }, [adminDashboardData?.adminDataCounts]);

  // Real activities data - empty array until API provides real activity data
  const recentActivities = useMemo(() => {
    // TODO: Replace with real API call for recent activities
    // For now, return empty array to show empty state
    return [];
  }, [adminDashboardData]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDates((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleStartDateChange = (date) => {
    if (date) {
      setDates((prevState) => ({
        ...prevState,
        startDate: moment(date).format("YYYY-MM-DD"),
      }));
    } else {
      setDates((prevState) => ({
        ...prevState,
        startDate: "",
      }));
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      setDates((prevState) => ({
        ...prevState,
        endDate: moment(date).format("YYYY-MM-DD"),
      }));
    } else {
      setDates((prevState) => ({
        ...prevState,
        endDate: "",
      }));
    }
  };

  const handleSearch = () => {
    if (!dates.startDate || !dates.endDate) {
      return;
    }
    setIsLoading(true);
    const formattedFromDate = moment(dates.startDate)
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss.SSSSSS");
    const formattedToDate = moment(dates.endDate)
      .endOf("day")
      .format("YYYY-MM-DD HH:mm:ss.SSSSSS");

    dispatch(
      getAdminDashboardData({
        startDate: formattedFromDate,
        endDate: formattedToDate,
      })
    );
    setSearchButtonClicked(true);
    setIsLoading(false);
  };

  const handleReset = () => {
    setIsLoading(true);
    setDates({ startDate: "", endDate: "" });
    dispatch(getAdminDashboardData({}));
    setSearchButtonClicked(false);
    setIsLoading(false);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    dispatch(
      getAdminDashboardData(
        searchButtonClicked
          ? {
              startDate: moment(dates.startDate)
                .startOf("day")
                .format("YYYY-MM-DD HH:mm:ss.SSSSSS"),
              endDate: moment(dates.endDate)
                .endOf("day")
                .format("YYYY-MM-DD HH:mm:ss.SSSSSS"),
            }
          : {}
      )
    );
    setIsLoading(false);
  };

  const isDateValid = (date) => {
    if (!date) return false;
    return moment(date, "YYYY-MM-DD", true).isValid();
  };

  // Export dashboard data to Excel
  const handleExportToExcel = () => {
    const data = adminDashboardData?.adminDataCounts || {};
    
    // Prepare data for export
    const exportData = [
      { Metric: "Total Employees", Count: data.totalEmployees || 0 },
      { Metric: "Total Managers", Count: data.totalManagers || 0 },
      { Metric: "Total Appraisals", Count: data.totalAssignedAppraisals || 0 },
      { Metric: "Total Templates", Count: data.totalTemplates || 0 },
      { Metric: "", Count: "" }, // Empty row
      { Metric: "Appraisal Status", Count: "" },
      { Metric: "Not Started", Count: data.notStarted || 0 },
      { Metric: "In Progress", Count: data.inProgress || 0 },
      { Metric: "Pending Manager Review", Count: data.completed || 0 },
      { Metric: "Under Review", Count: data.underReview || 0 },
      { Metric: "Need Discussion", Count: data.needDiscussion || 0 },
      { Metric: "Change Request", Count: data.changeRequest || 0 },
      { Metric: "Pending Final Review", Count: data.approved || 0 },
      { Metric: "Final Review", Count: data.finalReviewed || 0 },
      { Metric: "", Count: "" }, // Empty row
      { Metric: "Performance Ratings", Count: "" },
      { Metric: "Excellent", Count: data.excellent || 0 },
      { Metric: "Exceeds Expectations", Count: data.good || 0 },
      { Metric: "Meets Expectations", Count: data.average || 0 },
      { Metric: "Needs Improvement", Count: data.fair || 0 },
      { Metric: "Unsatisfactory", Count: data.poor || 0 },
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    worksheet["!cols"] = [{ wch: 30 }, { wch: 15 }];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dashboard Data");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `admin_dashboard_data_${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);
  };

  // Memoized calculations
  const dashboardMetrics = useMemo(() => {
    const data = adminDashboardData?.adminDataCounts || {};
    return {
      totalEmployees: (data.totalEmployees || 0) + (data.totalManagers || 0),
      totalAppraisals: data.totalAssignedAppraisals || 0,
      totalTemplates: data.totalTemplates || 0,
      totalApproved: data.approved || 0,
    };
  }, [adminDashboardData?.adminDataCounts]);

  const statusData = useMemo(() => {
    const data = adminDashboardData?.adminDataCounts || {};
    const statusArray = [
      { name: "Not Started", value: data.notStarted || 0 },
      { name: "In Progress", value: data.inProgress || 0 },
      { name: "Pending Manager Review", value: data.completed || 0 },
      { name: "Under Review", value: data.underReview || 0 },
      { name: "Need Discussion", value: data.needDiscussion || 0 },
      { name: "Change Request", value: data.changeRequest || 0 },
      { name: "Pending Final Review", value: data.approved || 0 },
      { name: "Final Review", value: data.finalReviewed || 0 },
    ];

    // Return all statuses regardless of value (show all statuses)
    return statusArray;
  }, [adminDashboardData?.adminDataCounts]);

  useEffect(() => {
    dispatch(getAdminDashboardData({}));
  }, [dispatch]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 min-h-screen">
      {/* Header Section */}

      {/* Main Metrics */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-8"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          <EnhancedMetricCard
            title="Total Employees"
            value={dashboardMetrics.totalEmployees}
            changeType="increase"
            icon={People}
            color="primary"
            subtitle="Active employees"
            filterData={[
              {
                label: "Employees",
                value: adminDashboardData?.adminDataCounts?.totalEmployees || 0,
                filterKey: "employees",
              },
              {
                label: "Managers",
                value: adminDashboardData?.adminDataCounts?.totalManagers || 0,
                filterKey: "managers",
              },
            ]}
            onFilterClick={(filterKey) => {
              // Navigate to filtered employee list
              console.log(`Filter by: ${filterKey}`);
            }}
          />
          <EnhancedMetricCard
            title="Total Appraisals"
            value={dashboardMetrics.totalAppraisals}
            changeType="increase"
            icon={Assignment}
            color="success"
            subtitle="Assigned this period"
            filterData={statusData.map((status) => ({
              label: status.name,
              value: status.value,
              filterKey: status.name.toLowerCase().replace(/\s+/g, "-"),
            }))}
            onFilterClick={(filterKey) => {
              // Navigate to filtered appraisal list
              console.log(`Filter appraisals by: ${filterKey}`);
            }}
          />
          <EnhancedMetricCard
            title="Templates"
            value={dashboardMetrics.totalTemplates}
            changeType="decrease"
            icon={Work}
            color="info"
            subtitle="Available templates"
          />
          <EnhancedMetricCard
            title="Pending Final Review"
            value={dashboardMetrics.totalApproved}
            changeType="increase"
            icon={Assessment}
            color="warning"
            subtitle="Pending final review appraisals"
            filterData={[
              {
                label: "Pending Final Review",
                value: adminDashboardData?.adminDataCounts?.approved || 0,
                filterKey: "pending-final-review",
              },
              {
                label: "Final Review",
                value: adminDashboardData?.adminDataCounts?.finalReviewed || 0,
                filterKey: "final-review",
              },
            ]}
            onFilterClick={(filterKey) => {
              // Navigate to filtered pending review list
              console.log(`Filter pending reviews by: ${filterKey}`);
            }}
          />
        </Box>
      </motion.div>

      {/* Charts and Analytics */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-8"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "1fr 1fr",
            },
            gap: 3,
          }}
        >
          <PerformanceTrendsChart data={performanceTrendData} />
          {/* Progress Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card
              className="h-full group"
              style={{
                background: "white",
                borderRadius: "24px",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-blue-50/30 to-pink-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 via-blue-600 to-pink-500 shadow-lg">
                    <Assessment style={{ color: "white", fontSize: "24px" }} />
                  </div>
                  <div>
                    <Typography variant="h6" className="font-bold text-gray-800 mb-0.5">
                      Appraisal Progress Overview
                    </Typography>
                    <Typography variant="caption" className="text-gray-500 text-xs">
                      Status breakdown by percentage
                    </Typography>
                  </div>
                </div>
                <div className="space-y-5">
                  {statusData.length > 0 ? (
                    statusData.map((status, index) => {
                      const total = statusData.reduce(
                        (sum, item) => sum + item.value,
                        0
                      );
                      const percentage =
                        total > 0 ? (status.value / total) * 100 : 0;
                      
                      // Use the same colors as in table data (from Utils.js)
                      const statusColorMap = {
                        "Not Started": "#B0B0B0", // Gray
                        "In Progress": "#2196F3", // Blue
                        "Pending Manager Review": "#009688", // Teal (was COMPLETED)
                        "Under Review": "#FF9800", // Orange
                        "Need Discussion": "#6e07f4", // Purple (NEED_DISCUSSION)
                        "Change Request": "#F44336", // Red
                        "Pending Final Review": "#66BB6A", // Green (was APPROVED)
                        "Final Review": "#9C27B0", // Purple (FINAL_REVIEWED)
                      };
                      
                      const color = statusColorMap[status.name] || "#8B5CF6";

                      return (
                        <Tooltip
                          key={status.name}
                          title={`Click to filter by ${status.name}`}
                          arrow
                          placement="top"
                        >
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="group/item cursor-pointer"
                            onClick={() => {
                              console.log(`Filter by status: ${status.name}`);
                              // TODO: Implement navigation to filtered appraisal list
                            }}
                            whileHover={{ x: 4 }}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <Typography
                                variant="body2"
                                className="font-semibold text-gray-700 group-hover/item:text-[#135e96] transition-colors"
                                style={{ fontSize: "14px" }}
                              >
                                {status.name}
                              </Typography>
                              <div className="flex items-center gap-2">
                                <Typography
                                  variant="body2"
                                  className="font-bold"
                                  style={{ color, fontSize: "14px" }}
                                >
                                  {status.value}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  className="text-gray-500"
                                  style={{ fontSize: "12px" }}
                                >
                                  ({percentage.toFixed(1)}%)
                                </Typography>
                              </div>
                            </div>
                            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                              <motion.div
                                className="h-full rounded-full group-hover/item:opacity-90 transition-opacity"
                                style={{ backgroundColor: color }}
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 1, delay: index * 0.1 }}
                              />
                              <div
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                                style={{
                                  animation: "shimmer 2s infinite",
                                }}
                              ></div>
                            </div>
                          </motion.div>
                        </Tooltip>
                      );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                        <Assessment style={{ color: "#8B5CF6", fontSize: "32px" }} />
                      </div>
                      <Typography variant="body2" className="text-gray-500 font-medium">
                        No appraisal data available
                      </Typography>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </Box>
      </motion.div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-8">
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <Typography variant="body1">Loading dashboard data...</Typography>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EnhancedAdminDashboard;
